// Coworking costume v1 — every piece of coworking-flavoured language for Jigyasa lives here.
// If you want a bakery / clinic / school version of Jigyasa, copy this file and swap the words.
// The engine code (state.js, OperatorDesktop.js, etc.) must never hardcode "seat" or "hub".

export const COWORKING_COSTUME = {
  costumeId: "coworking_v1",

  // The seven stages — engine knows the keys, the costume knows the labels.
  // 11 Jun 2026 (pilot feedback): "Sent price" renamed to "Sent offer letter".
  // The engine key `sent_price` is unchanged — labels are costume territory.
  stages: [
    { key: "new", label: "New" },
    { key: "spoke", label: "Spoke to them" },
    { key: "visit_scheduled", label: "Visit scheduled" },
    { key: "visited", label: "Visited" },
    { key: "sent_price", label: "Sent offer letter" },
    { key: "signed_up", label: "Signed up" },
    { key: "lost", label: "Lost" },
  ],

  stageColors: {
    new: "bg-amber-100 text-amber-800 border-amber-200",
    spoke: "bg-orange-100 text-orange-800 border-orange-200",
    visit_scheduled: "bg-yellow-100 text-yellow-800 border-yellow-200",
    visited: "bg-emerald-100 text-emerald-800 border-emerald-200",
    sent_price: "bg-rose-100 text-rose-800 border-rose-200",
    signed_up: "bg-green-100 text-green-800 border-green-200",
    lost: "bg-zinc-200 text-zinc-700 border-zinc-300",
  },

  // 11 Jun 2026: sources are now ORG-LEVEL configuration, not costume
  // configuration. Two coworking brands can run different lists. This array
  // is only the industry's *default suggestion* shown at setup time; the live
  // list lives in org settings (see ORG_SOURCES in data.js for the pilot) and
  // is editable via Settings → Manage enquiry sources.
  defaultSources: [
    "Walk-in",
    "Phone call",
    "Website",
    "Google maps",
    "Social media",
    "Others",
  ],

  lostReasons: [
    "Too costly",
    "Wanted full office, not coworking",
    "Found own place",
    "Location not right",
    "Went to a competitor",
    "Stopped responding",
    "Project on hold / not happening",
    "Other",
  ],

  enquiryTypes: [
    "Single seat",
    "Open seats (team)",
    "Cabin",
    "Meeting room",
    "Day pass",
    "Virtual office",
  ],

  // Mapping from operator-facing display label → host enum key.
  // Locked with Kreta 10 Jun 2026: host enum is 5 values for pricing/billing purposes.
  // "Single seat" and "Open seats (team)" both map to general_seat (same per-seat rate);
  // the distinction is captured separately via seat_count for intent.
  // Virtual office is a real Wekreate product but not yet captured in Kreta — v1 pricing returns null.
  enquiryTypeHostEnum: {
    "Single seat": "general_seat",
    "Open seats (team)": "general_seat",
    "Cabin": "cabin_seat",
    "Meeting room": "meeting_room",
    "Day pass": "day_pass",
    "Virtual office": "virtual_office",
  },

  waTemplates: [
    { name: "First contact", body: "Hi {{name}}, this is {{me}} from {{company}}. Thanks for your interest. Would you like to drop by for a quick walkthrough?" },
    { name: "Visit invite", body: "Hi {{name}}, sharing our address: {{address}}. We're open 9am–7pm Mon–Sat. Whenever works for you, send a message before you start." },
    { name: "Offer letter share", body: "Hi {{name}}, sharing the offer as discussed: {{quote}}. Let me know if you'd like to confirm." },
    { name: "Soft follow-up", body: "Hi {{name}}, just checking in — any thoughts?" },
    { name: "Polite drop", body: "Hi {{name}}, hope all's well. Closing this for now; whenever you're ready, ping me." },
  ],

  companyInfo: {
    name: "DemoSpace Coworking",
    me: "Anita",
    address: "Demo Towers, MG Road, Demo City",
  },

  // Industry vocabulary used on screen.
  vocab: {
    unit: "seat",
    units: "seats",
    location: "hub",
    locations: "hubs",
    customer: "client",
    quantityFieldLabel: "How many seats?",
  },

  // All user-facing copy — pulled into one place so a future costume changes a single file.
  copy: {
    appName: "Jigyasa",
    tagline: "An enquiries tracker for businesses that run on phones.",
    landingHint: "Add ?demo=1 to the URL to open the clickable demo.",
    landingNoteLine1: "जिज्ञासा",
    landingNoteLine2: "Pilot preview · not for public release",

    demoBannerLabel: "Demo mode",
    demoBannerNote: "refresh wipes everything. Sample data only.",

    operatorHomeTitle: "Today",
    operatorSignedInAs: "Signed in as Anita · DemoSpace Coworking",
    ownerDashboardTitle: "Your business, today",
    ownerCompanyLine: "DemoSpace Coworking",

    sectionCallBackToday: "Call back today",
    sectionNewToday: "New today",
    sectionEveryoneElse: "Everyone else",
    sectionPipeline: "Pipeline",
    sectionSmartSuggestions: "Smart suggestions",
    sectionCapacitySnapshot: "Capacity snapshot",
    sectionRenewalRadar: "Renewal radar — next 60 days",
    sectionTrend: "Enquiries vs signed up — last 6 months",
    sectionSources: "Where leads come from (30 days)",
    sectionLost: "Why we lose (30 days)",
    sectionWins: "Recent wins",

    // Owner attention strip (pilot feedback, 11 Jun 2026)
    sectionAttention: "Needs your attention",
    attentionAllClear: "All caught up — nothing is waiting on you.",
    attentionWaitingQuote: "waiting after offer letter",

    // Remarks timeline (pilot feedback, 11 Jun 2026)
    remarksTitle: "Remarks & history",
    remarkPlaceholder: "e.g. Busy today — call back tomorrow",
    addRemarkButton: "Add",
    remarksEmpty: "No remarks yet — add one after each call so the story stays in one place.",

    // Org settings (pilot feedback, 11 Jun 2026)
    settingsSourcesTitle: "Manage enquiry sources",
    settingsSourcesNote: "Each organisation sets up its own list of where enquiries come from. Removing a source never changes past enquiries — it only affects new ones.",

    addEnquiryButton: "Add new enquiry",
    newEnquirySheetTitle: "New enquiry",
    convertedSheetTitle: "Lead converted",
    lostSheetTitle: "Why was this lost?",
    waSheetTitle: "Pick a WhatsApp template",
    waSheetNote: "Demo only — nothing is sent. In the real build this opens WhatsApp with the message pre-filled.",

    // Intelligence panels (the six data buttons)
    capacityPanelTitle: "Where they fit",
    capacityPanelNoFit: "No single hub fits this ask right now.",
    capacityPanelBestFit: "Best single-hub fit",
    capacityPanelUpcoming: "Freeing up in next 60 days",
    capacityPanelPhased: "Phased option",

    footprintBannerLabel: "Already with us",
    footprintBannerPrefix: "This contact's company",
    footprintInlineDismiss: "This is someone new — clear and start fresh",
    pastEnquiryBannerLabel: "Returning lead",
    pastEnquiryBannerNote: "We've spoken before — don't start cold.",

    pricingPanelTitle: "Pricing context",
    pricingPanelStandard: "Standard rate",
    pricingPanelRecentCloses: "Recent comparable deals",
    pricingPanelOffersActive: "Active offers",
    pricingPanelDiscountLimit: "Your discount authority",

    renewalRadarLeaseEnds: "lease ends in",
    renewalRadarGrowing: "showing growth signals",
    renewalRadarSteady: "stable",
    renewalRadarShrinking: "showing churn signals",

    smartSuggestionExpansion: "Likely outgrowing their plan",
    smartSuggestionLapsedLead: "Lapsed lead — space just opened",
  },

  // Which of the twelve buttons are switched on for this costume.
  // Used to conditionally render intelligence panels in the UI.
  buttonsOn: {
    liveStock: true,         // 1
    upcomingStock: true,     // 2
    customerFootprint: true, // 3
    pastEnquiries: true,     // 4 — Jigyasa-side, not host-side
    pricingAndOffers: true,  // 5
    activityHistory: false,  // 6
    renewalPipeline: true,   // 7
    locationCatalog: false,  // 8
    satisfaction: false,     // 9
    availability: false,     // 10
    compliance: false,       // 11
    communications: false,   // 12
  },
};
